import React from 'react';
import { Plus, Trash, Sparkle, MusicNote, FileText, Translate, BookOpen } from '@phosphor-icons/react';

export default function GhazalModule({ metadata, onChange }) {
  const handleChange = (field, value) => {
    onChange({ ...metadata, [field]: value });
  };

  const handleSherChange = (index, value) => {
    const newShers = [...(metadata.shers || [])];
    newShers[index] = value;
    handleChange('shers', newShers);
  };

  const addSher = () => {
    handleChange('shers', [...(metadata.shers || []), '']);
  };

  const removeSher = (index) => {
    const newShers = (metadata.shers || []).filter((_, i) => i !== index);
    handleChange('shers', newShers);
  };

  return (
    <div className="space-y-6">
      {/* Matla (Opening Couplet) */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Sparkle size={16} weight="fill" className="text-amber-500" />
          Matla (मतला) - Opening Couplet
        </label>
        <textarea
          value={metadata.matla || ''}
          onChange={(e) => handleChange('matla', e.target.value)}
          placeholder="पहला शेर (मतला) लिखें..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none text-lg"
          rows={3}
          style={{ fontFamily: 'Noto Sans Devanagari, serif', lineHeight: '2' }}
          dir="auto"
        />
      </div>

      {/* Shers (Couplets) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium flex items-center gap-2">
            <FileText size={16} />
            Shers (शेर) <span className="text-red-500">*</span>
          </label>
          <button
            onClick={addSher}
            className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-primary text-primary-foreground hover:opacity-90"
          >
            <Plus size={14} weight="bold" />
            Add Sher
          </button>
        </div>

        {(metadata.shers || []).length === 0 && (
          <button
            onClick={addSher}
            className="w-full p-8 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors"
          >
            <div className="text-center">
              <Sparkle size={32} className="mx-auto mb-2 text-muted-foreground" weight="fill" />
              <p className="text-sm text-muted-foreground">अपनी ग़ज़ल के शेर जोड़ें</p>
            </div>
          </button>
        )}

        <div className="space-y-4">
          {(metadata.shers || []).map((sher, index) => (
            <div key={index} className="relative group">
              <div className="absolute -left-8 top-2 text-xs text-muted-foreground font-mono">
                {index + 1}
              </div>
              <textarea
                value={sher}
                onChange={(e) => handleSherChange(index, e.target.value)}
                placeholder="शेर लिखें (दो मिसरे)..."
                className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none text-lg"
                rows={3}
                style={{ fontFamily: 'Noto Sans Devanagari, serif', lineHeight: '2' }}
                dir="auto"
              />
              {(metadata.shers || []).length > 1 && (
                <button
                  onClick={() => removeSher(index)}
                  className="absolute -right-10 top-2 p-2 rounded-lg text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
                >
                  <Trash size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Maqta (Closing Couplet with Pen Name) */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Sparkle size={16} weight="fill" className="text-purple-500" />
          Maqta (मक़्ता) - Closing Couplet (with pen name)
        </label>
        <textarea
          value={metadata.maqta || ''}
          onChange={(e) => handleChange('maqta', e.target.value)}
          placeholder="आखिरी शेर जिसमें तख़ल्लुस (कलमी नाम) हो..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none text-lg"
          rows={3}
          style={{ fontFamily: 'Noto Sans Devanagari, serif', lineHeight: '2' }}
          dir="auto"
        />
      </div>

      {/* Radif & Qaafiya */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Radif (रदीफ़) - Repeated Word/Phrase
          </label>
          <input
            type="text"
            value={metadata.radif || ''}
            onChange={(e) => handleChange('radif', e.target.value)}
            placeholder="e.g., है, हूँ, था"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
          <p className="text-xs text-muted-foreground mt-1">
            The word that repeats at the end of each line
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">
            Qaafiya (क़ाफ़िया) - Rhyme Scheme
          </label>
          <input
            type="text"
            value={metadata.qaafiya || ''}
            onChange={(e) => handleChange('qaafiya', e.target.value)}
            placeholder="e.g., -aar, -een"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
          <p className="text-xs text-muted-foreground mt-1">
            The rhyming pattern before radif
          </p>
        </div>
      </div>

      {/* Bahr (Meter) */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Bahr (बह्र) - Meter/Rhythm (Optional)
        </label>
        <input
          type="text"
          value={metadata.bahr || ''}
          onChange={(e) => handleChange('bahr', e.target.value)}
          placeholder="e.g., बह्रे रमल, बह्रे हज़ज"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Classical poetic meter
        </p>
      </div>

      {/* Poet's Notes */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <BookOpen size={16} />
          Poet's Notes (शायर की टिप्पणी)
        </label>
        <textarea
          value={metadata.poet_notes || ''}
          onChange={(e) => handleChange('poet_notes', e.target.value)}
          placeholder="Share your thoughts about this ghazal..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={3}
        />
      </div>

      {/* Translation */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Translate size={16} />
          Translation (Optional)
        </label>
        <textarea
          value={metadata.translation || ''}
          onChange={(e) => handleChange('translation', e.target.value)}
          placeholder="English or Hindi translation..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={4}
        />
      </div>

      {/* Audio Recitation */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <MusicNote size={16} />
          Audio Recitation URL
        </label>
        <input
          type="url"
          value={metadata.audio_url || ''}
          onChange={(e) => handleChange('audio_url', e.target.value)}
          placeholder="https://example.com/audio.mp3"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Upload audio to a hosting service and paste the URL here
        </p>
      </div>
    </div>
  );
}
